import { StorageManager } from '../../../../common/storage-manager.class';
import type {
    ReactiveDropdownDataSource,
    ReactiveDropdownDataSourceContext,
    ReactiveDropdownDependencyBehaviour,
    ReactiveDropdownOptionFilter,
    DropdownFilter,
    DropdownData,
    DropdownFilterContext,
    FilteredValue,
    Value,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveSelectionResolver } from '@fitogest/reactive-form-builder/utils';

type LabelSelector<TEntity> =
    keyof TEntity & string |
    readonly (keyof TEntity & string)[] |
    ((item: TEntity) => unknown) |
    { readonly or: readonly (keyof TEntity & string)[] };


export type TareasClientScopedLegacyFilterOptions = {
    readonly clientFieldName?: string;
    readonly isEnabled?: () => boolean;
};

export type TareasParcelasScopedLegacyFilterOptions = {
    readonly fincaFieldName?: string;
    readonly sectorFieldName?: string;
    readonly isSectorFilterEnabled?: () => boolean;
    readonly requireActive?: boolean;
};

export type TareasClientScopedOptionFilterOptions = TareasClientScopedLegacyFilterOptions;

export type TareasParcelasScopedOptionFilterOptions = TareasParcelasScopedLegacyFilterOptions;

export type TareasActiveCatalogueLegacyFilterOptions<TEntity extends object> = {
    readonly activeFieldName?: keyof TEntity & string;
    readonly activeValue?: unknown;
    readonly requireActive?: boolean;
    readonly onAccepted?: (option: FilteredValue<TEntity>) => void;
};

export type TareasClientCatalogueLegacyFilterOptions<TFilter, TEntity extends object> = TareasActiveCatalogueLegacyFilterOptions<TEntity> & {
    readonly clientFieldName?: keyof TEntity & string;
    readonly getClientId?: (context: DropdownFilterContext<TFilter> | undefined) => unknown;
    readonly isEnabled?: () => boolean;
};

export type TareasFieldEqualsLegacyFilterOptions<TEntity extends object> = {
    readonly fieldName: keyof TEntity & string;
    readonly value: unknown;
};

export type TareasLocationDropdownDataSourceOptions<TModel, TEntity extends object> = {
    readonly endpoint: unknown;
    readonly cacheKey: string;
    readonly label: LabelSelector<TEntity>;
    readonly dependsOn?: readonly string[];
    readonly params?: (context: ReactiveDropdownDataSourceContext<TModel>) => Record<string, unknown>;
    readonly filterOption?: (option: Value<unknown>, context: ReactiveDropdownDataSourceContext<TModel>) => boolean;
    readonly distinctBy?: ReactiveDropdownDataSource<TModel>['distinctBy'];
    readonly ttlMs?: number;
};

export type TareasCatalogueDropdownSort = (first: Value<unknown>, second: Value<unknown>) => number;

export type TareasCatalogueDropdownDataSourceOptions<TModel, TEntity extends object> = {
    readonly endpoint: unknown;
    readonly cacheKey: string;
    readonly label: LabelSelector<TEntity>;
    readonly noPlaceholder?: boolean;
    readonly lazy?: boolean;
    readonly dependsOn?: readonly string[];
    readonly params?: (context: ReactiveDropdownDataSourceContext<TModel>) => Record<string, unknown>;
    readonly filterOption?: (option: Value<unknown>, context: ReactiveDropdownDataSourceContext<TModel>) => boolean;
    readonly sortOptions?: TareasCatalogueDropdownSort;
    readonly distinctBy?: ReactiveDropdownDataSource<TModel>['distinctBy'];
    readonly ttlMs?: number;
};

const DEFAULT_LOCATION_CACHE_TTL_MS = 300000;
const DEFAULT_CATALOGUE_CACHE_TTL_MS = 300000;
const DEFAULT_CATALOGUE_PLACEHOLDER_LABEL = '...';
const SIN_VARIETY_LABEL = 'SIN VARIEDAD';

export const tareasLocationDependencyBehaviour: ReactiveDropdownDependencyBehaviour = {
    clearOnHydration: false,
    keepCurrentValueIfStillAvailable: true,
    keepValueUntilOptionsLoaded: true,
    preserveValueWhenOptionsMissing: true,
    preservePanelOnReload: true,
    preserveFilterOnReload: true,
    showLoadingOptionDuringReload: true,
    reloadOnParentChange: false,
    filterLocally: true,
};

export const tareasLocationFincaDependencyBehaviour: ReactiveDropdownDependencyBehaviour = {
    ...tareasLocationDependencyBehaviour,
    autoSelect: 'singleOption',
};

/** Creates a typed declarative dropdown data source for task location catalogues. */
export function createTareasLocationDropdownDataSource<TModel, TEntity extends object>(
    options: TareasLocationDropdownDataSourceOptions<TModel, TEntity>,
): ReactiveDropdownDataSource<TModel> {
    return {
        endpoint: options.endpoint,
        cacheKey: options.cacheKey,
        ...(options.dependsOn !== undefined ? { dependsOn: options.dependsOn } : {}),
        ...(options.params !== undefined ? { params: options.params } : {}),
        ...(options.filterOption !== undefined ? { filterOption: options.filterOption } : {}),
        ttlMs: options.ttlMs ?? DEFAULT_LOCATION_CACHE_TTL_MS,
        cacheEmptyResults: true,
        distinctBy: options.distinctBy ?? 'id',
        mapOption: (item: unknown) => mapLocationOption(item, options.label),
    };
}

export function createTareasCatalogueDropdownDataSource<TModel, TEntity extends object>(
    options: TareasCatalogueDropdownDataSourceOptions<TModel, TEntity>,
): ReactiveDropdownDataSource<TModel> {
    return {
        endpoint: options.endpoint,
        cacheKey: options.cacheKey,
        ...(options.lazy !== undefined ? { lazy: options.lazy } : {}),
        ...(options.dependsOn !== undefined ? { dependsOn: options.dependsOn } : {}),
        ...(options.params !== undefined ? { params: options.params } : {}),
        ...(options.filterOption !== undefined ? { filterOption: options.filterOption } : {}),
        ttlMs: options.ttlMs ?? DEFAULT_CATALOGUE_CACHE_TTL_MS,
        distinctBy: options.distinctBy ?? 'id',
        mapResponse: (response: unknown) => buildCatalogueDropdownData(response, options),
    };
}

export function createClientScopedOptionFilter<TModel>(
    options: TareasClientScopedOptionFilterOptions = {},
): ReactiveDropdownOptionFilter<TModel> {
    return (option, context): boolean => {
        const optionValue = option.value as { readonly id_cliente?: unknown } | null;

        if (options.isEnabled?.() === false || optionValue === null) {
            return true;
        }

        const clientId = getContextEntityId(
            context,
            options.clientFieldName ?? 'id_cliente',
            StorageManager.getClient()?.id ?? null,
        );

        return isUnscopedOrSameEntity(optionValue.id_cliente, clientId);
    };
}

export function createFincaScopedOptionFilter<TModel>(
    fincaFieldName = 'id_finca',
): ReactiveDropdownOptionFilter<TModel> {
    return (option, context): boolean => {
        const optionValue = option.value as { readonly id_finca?: unknown } | null;

        if (optionValue === null) {
            return true;
        }

        const fincaId = getContextEntityId(context, fincaFieldName, null);

        return fincaId !== null && String(optionValue.id_finca ?? '') === String(fincaId);
    };
}

export function createParcelasScopedOptionFilter<TModel>(
    options: TareasParcelasScopedOptionFilterOptions = {},
): ReactiveDropdownOptionFilter<TModel> {
    return (option, context): boolean => {
        const optionValue = option.value as { readonly activo?: unknown; readonly id_finca?: unknown; readonly id_sector?: unknown } | null;

        if (optionValue === null) {
            return true;
        }

        const requireActive = options.requireActive ?? true;

        if (requireActive && optionValue.activo?.toString() !== '1') {
            return false;
        }

        const sectorFieldName = options.sectorFieldName ?? 'id_sector';
        const isSectorFilterEnabled = options.isSectorFilterEnabled?.() ?? true;
        const sectorIds = isSectorFilterEnabled ? getContextEntityIds(context, sectorFieldName) : [];

        if (sectorIds.length > 0) {
            return sectorIds.includes(String(optionValue.id_sector ?? ''));
        }

        const fincaId = getContextEntityId(context, options.fincaFieldName ?? 'id_finca', null);

        return fincaId !== null && String(optionValue.id_finca ?? '') === String(fincaId);
    };
}


export function createActiveCatalogueLegacyFilter<
    TFilter = unknown,
    TEntity extends object = TFilter extends object ? TFilter : Record<string, unknown>,
>(
    options: TareasActiveCatalogueLegacyFilterOptions<TEntity> = {},
): DropdownFilter<TFilter> {
    return (option): boolean => {
        const typedOption = option as unknown as FilteredValue<TEntity>;
        const optionValue = typedOption.value as Record<string, unknown> | null;

        if (optionValue === null) {
            return true;
        }

        const requireActive = options.requireActive ?? true;

        if (requireActive) {
            const activeFieldName = options.activeFieldName ?? ('activo' as keyof TEntity & string);
            const activeValue = options.activeValue ?? '1';

            if (String(optionValue[activeFieldName] ?? '') !== String(activeValue)) {
                return false;
            }
        }

        options.onAccepted?.(typedOption);

        return true;
    };
}

export function createClientScopedCatalogueLegacyFilter<
    TFilter = unknown,
    TEntity extends object = TFilter extends object ? TFilter : Record<string, unknown>,
>(
    options: TareasClientCatalogueLegacyFilterOptions<TFilter, TEntity> = {},
): DropdownFilter<TFilter> {
    return (option, context): boolean => {
        const activeFilter = createActiveCatalogueLegacyFilter<TFilter, TEntity>(options);

        if (!activeFilter(option, context)) {
            return false;
        }

        if (options.isEnabled?.() === false) {
            return true;
        }

        const optionValue = option.value as Record<string, unknown> | null;

        if (optionValue === null) {
            return true;
        }

        const clientId = extractEntityId(options.getClientId?.(context)) ?? extractEntityId(StorageManager.getClient()?.id ?? null);

        if (clientId === null) {
            return true;
        }

        const clientFieldName = options.clientFieldName ?? ('id_cliente' as keyof TEntity & string);

        return isUnscopedOrSameEntity(optionValue[clientFieldName], clientId);
    };
}

export function createFieldEqualsLegacyFilter<
    TFilter = unknown,
    TEntity extends object = TFilter extends object ? TFilter : Record<string, unknown>,
>(
    options: TareasFieldEqualsLegacyFilterOptions<TEntity>,
): DropdownFilter<TFilter> {
    return (option): boolean => {
        const optionValue = option.value as Record<string, unknown> | null;

        if (optionValue === null) {
            return true;
        }

        return String(optionValue[options.fieldName] ?? '') === String(options.value);
    };
}


export function createClientScopedLegacyFilter<TModel>(
    options: TareasClientScopedLegacyFilterOptions = {},
): DropdownFilter<TModel> {
    return (option, context): boolean => {
        const optionValue = option.value as { readonly id_cliente?: unknown } | null;

        if (options.isEnabled?.() === false || optionValue === null) {
            return true;
        }

        const clientId = getFilterContextEntityId(
            context,
            options.clientFieldName ?? 'id_cliente',
            StorageManager.getClient()?.id ?? null,
        );

        return isUnscopedOrSameEntity(optionValue.id_cliente, clientId);
    };
}

export function createFincaScopedLegacyFilter<TModel>(
    fincaFieldName = 'id_finca',
): DropdownFilter<TModel> {
    return (option, context): boolean => {
        const optionValue = option.value as { readonly id_finca?: unknown } | null;

        if (optionValue === null) {
            return true;
        }

        const fincaId = getFilterContextEntityId(context, fincaFieldName, null);

        return fincaId !== null && String(optionValue.id_finca ?? '') === String(fincaId);
    };
}

export function createParcelasScopedLegacyFilter<TModel>(
    options: TareasParcelasScopedLegacyFilterOptions = {},
): DropdownFilter<TModel> {
    return (option, context): boolean => {
        const optionValue = option.value as { readonly activo?: unknown; readonly id_finca?: unknown; readonly id_sector?: unknown } | null;

        if (optionValue === null) {
            return true;
        }

        const requireActive = options.requireActive ?? true;

        if (requireActive && optionValue.activo?.toString() !== '1') {
            return false;
        }

        const sectorFieldName = options.sectorFieldName ?? 'id_sector';
        const isSectorFilterEnabled = options.isSectorFilterEnabled?.() ?? true;
        const sectorIds = isSectorFilterEnabled ? getFilterContextEntityIds(context, sectorFieldName) : [];

        if (sectorIds.length > 0) {
            return sectorIds.includes(String(optionValue.id_sector ?? ''));
        }

        const fincaId = getFilterContextEntityId(context, options.fincaFieldName ?? 'id_finca', null);

        return fincaId !== null && String(optionValue.id_finca ?? '') === String(fincaId);
    };
}

export function createClientScopedParams<TModel>(
    context: ReactiveDropdownDataSourceContext<TModel>,
): Record<string, unknown> {
    const clientId = getContextEntityId(context, 'id_cliente', StorageManager.getClient()?.id ?? null);

    return clientId === null ? {} : { id_cliente: clientId };
}

export function createFincaScopedParams<TModel>(
    context: ReactiveDropdownDataSourceContext<TModel>,
): Record<string, unknown> {
    const fincaId = getContextEntityId(context, 'id_finca', null);

    return fincaId === null ? {} : { id_finca: fincaId };
}

export function createClientAndFincaScopedParams<TModel>(
    context: ReactiveDropdownDataSourceContext<TModel>,
): Record<string, unknown> {
    const params: Record<string, unknown> = createClientScopedParams(context);
    const fincaId = getContextEntityId(context, 'id_finca', null);

    if (fincaId !== null) {
        params['id_finca'] = fincaId;
    }

    return params;
}

export function getContextEntityId<TModel>(
    context: ReactiveDropdownDataSourceContext<TModel>,
    fieldName: string,
    fallback: unknown,
): string | number | null {
    const backendValue = context.getSelectedBackendValue(fieldName);
    const rawValue = context.getSelectedRaw(fieldName);
    const controlValue = context.model?.get(fieldName)?.value;

    return extractEntityId(backendValue) ?? extractEntityId(rawValue) ?? extractEntityId(controlValue) ?? extractEntityId(fallback);
}

export function getContextEntityIds<TModel>(
    context: ReactiveDropdownDataSourceContext<TModel>,
    fieldName: string,
): string[] {
    return ReactiveSelectionResolver.extractIdList([
        context.getSelectedBackendValue(fieldName),
        context.getSelectedRaw(fieldName),
        context.model?.get(fieldName)?.value,
    ]);
}

export function extractEntityId(value: unknown): string | number | null {
    return ReactiveSelectionResolver.extractId(value);
}


function isUnscopedOrSameEntity(value: unknown, selectedId: string | number | null): boolean {
    return selectedId === null || String(value ?? '') === String(selectedId);
}

function getFilterContextEntityId<TEntity>(
    context: DropdownFilterContext<TEntity> | undefined,
    fieldName: string,
    fallback: unknown,
): string | number | null {
    const parentValue = context?.changedField === fieldName ? context.parentValue : null;
    const modelValue = context?.model?.get(fieldName)?.value;

    return extractEntityId(parentValue) ?? extractEntityId(modelValue) ?? extractEntityId(fallback);
}

function getFilterContextEntityIds<TEntity>(
    context: DropdownFilterContext<TEntity> | undefined,
    fieldName: string,
): string[] {
    return ReactiveSelectionResolver.extractIdList([
        context?.changedField === fieldName ? context.parentValue : null,
        context?.model?.get(fieldName)?.value,
    ]);
}

function buildCatalogueDropdownData<TModel, TEntity extends object>(
    response: unknown,
    options: TareasCatalogueDropdownDataSourceOptions<TModel, TEntity>,
): DropdownData<unknown> {
    const distinctLabels = new Set<string>();
    const catalogueOptions = toCatalogueItems<TEntity>(response)
        .map((item) => mapCatalogueOption(item, options.label))
        .filter((option) => {
            if (distinctLabels.has(option.label)) {
                return option.label === SIN_VARIETY_LABEL;
            }

            distinctLabels.add(option.label);

            return true;
        })
        .sort(options.sortOptions ?? sortCatalogueOptionsByLabel);
    const values = options.noPlaceholder === true
        ? catalogueOptions
        : [{ label: DEFAULT_CATALOGUE_PLACEHOLDER_LABEL, value: null }, ...catalogueOptions];

    return {
        values,
        filtered: [...values],
    };
}

function toCatalogueItems<TEntity extends object>(response: unknown): TEntity[] {
    const values = Array.isArray(response) ? response : [response];

    return values.filter((item): item is TEntity => item !== null && typeof item === 'object');
}

function mapCatalogueOption<TEntity extends object>(item: TEntity, label: LabelSelector<TEntity>): Value<unknown> {
    const resolvedLabel = resolveLabel(item, label);
    const finalLabel = resolvedLabel !== '' ? resolvedLabel : resolveFallbackLabel(item);

    return {
        label: finalLabel,
        value: item,
        raw: item,
        valueAliases: getDropdownAliases(item, finalLabel),
    };
}

function sortCatalogueOptionsByLabel(first: Value<unknown>, second: Value<unknown>): number {
    return first.label.localeCompare(second.label, undefined, { sensitivity: 'base' });
}

function getDropdownAliases<TEntity extends object>(item: TEntity, label: string): readonly unknown[] {
    const record = item as Record<string, unknown>;
    const aliases = [
        record['id'],
        record['codigo'],
        record['cod_producto'],
        record['codigo_producto'],
        record['nombre'],
        record['razon_social'],
        record['descripcion'],
        label,
    ];

    return aliases.filter(isMeaningfulValue);
}

function mapLocationOption<TEntity extends object>(item: unknown, label: LabelSelector<TEntity>): Value<unknown> {
    const entity = item as TEntity;
    const resolvedLabel = resolveLabel(entity, label);

    return {
        label: resolvedLabel !== '' ? resolvedLabel : resolveFallbackLabel(entity),
        value: item,
        raw: item,
    };
}

function resolveLabel<TEntity extends object>(item: TEntity, label: LabelSelector<TEntity>): string {
    if (typeof label === 'function') {
        return String(label(item) ?? '');
    }

    if (Array.isArray(label)) {
        return label.map((fieldName) => readEntityField(item, fieldName)).filter(isMeaningfulValue).join(' ');
    }

    if (isOrLabelSelector(label)) {
        for (const fieldName of label.or) {
            const value = readEntityField(item, fieldName);

            if (isMeaningfulValue(value)) {
                return String(value);
            }
        }

        return '';
    }

    if (typeof label === 'string') {
        return String(readEntityField(item, label) ?? '');
    }

    return '';
}

function resolveFallbackLabel<TEntity extends object>(item: TEntity): string {
    const record = item as Record<string, unknown>;
    const candidates = [
        record['label'],
        record['nombre'],
        record['name'],
        record['descripcion'],
        record['codigo'],
        record['id'],
    ];

    for (const candidate of candidates) {
        if (isMeaningfulValue(candidate)) {
            return String(candidate);
        }
    }

    return '';
}

function isOrLabelSelector<TEntity extends object>(
    label: LabelSelector<TEntity>,
): label is { readonly or: readonly (keyof TEntity & string)[] } {
    return typeof label === 'object' && label !== null && !Array.isArray(label) && 'or' in label;
}

function isMeaningfulValue(value: unknown): boolean {
    return value !== null && value !== undefined && String(value).trim().length > 0;
}

function readEntityField<TEntity extends object>(item: TEntity, fieldName: keyof TEntity & string): unknown {
    return (item as Record<string, unknown>)[fieldName];
}
